import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { useCart } from '../../contexts/CartContext';
import { db, collection, getDocs, query, where } from '../../firebase/config';
import ItemDetailModal from '../../components/ItemDetailModal';
import BookingCalendarModal from '../../components/BookingCalendarModal';
import {
  Search, Filter, Star, MapPin, Scissors, Sparkles,
  Droplets, Clock, Heart, User, Plus, Package,
  Car, ChevronRight, X, Navigation,
} from 'lucide-react';

// Haversine (inline fallback in case LocationContext doesn't export one usable here)
const haversineKm = (lat1, lng1, lat2, lng2) => {
  if ([lat1, lng1, lat2, lng2].some(v => v == null)) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

// Matches HomePage's NEAR_YOU_RADIUS_KM so "See all near me" is consistent
const NEAR_ME_RADIUS_KM = 25;

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { currentLocation } = useLocation();
  const { addToCart } = useCart();

  const [pros, setPros] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeType, setActiveType] = useState('all');
  const [nearMeOnly, setNearMeOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  // Auto-enable "Near Me" if arriving via /dashboard/search?near=1
  useEffect(() => {
    if (searchParams.get('near') === '1' && currentLocation?.lat) {
      setNearMeOnly(true);
    }
  }, [searchParams, currentLocation]);

  const [toast, setToast] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [bookingService, setBookingService] = useState(null);
  const [bookingPro, setBookingPro] = useState(null);

  const categories = [
    { id: 'All', icon: <Sparkles size={18} />, label: 'All' },
    { id: 'Hair', icon: <Scissors size={18} />, label: 'Hair' },
    { id: 'Nails', icon: <Sparkles size={18} />, label: 'Nails' },
    { id: 'Skin', icon: <Droplets size={18} />, label: 'Skin' },
    { id: 'Lashes', icon: <Clock size={18} />, label: 'Lashes' },
    { id: 'Spa', icon: <Heart size={18} />, label: 'Spa' },
  ];

  const types = [
    { id: 'all', label: 'All' },
    { id: 'services', label: 'Services' },
    { id: 'products', label: 'Products' },
  ];

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2000);
  };

  // ---------- Load data ----------
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      try {
        const [prosSnap, servicesSnap, productsSnap] = await Promise.all([
          getDocs(query(collection(db, 'users'), where('role', '==', 'professional'))),
          getDocs(collection(db, 'services')),
          getDocs(collection(db, 'products')),
        ]);

        if (cancelled) return;

        const prosData = prosSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        // REMOVED the filter hiding your own items, so you can see your work
        const servicesData = servicesSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => s.active !== false)
          .map(s => {
            const pro = prosData.find(p => p.id === s.professionalId);
            return { ...s, pro };
          });

        const productsData = productsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.active !== false)
          .map(p => {
            const pro = prosData.find(pr => pr.id === p.professionalId);
            return { ...p, pro };
          });

        setPros(prosData);
        setServices(servicesData);
        setProducts(productsData);

        const active = prosData
          .filter(p => p.isOnline === true || p.status === 'online' || p.active === true)
          .slice(0, 15);
        setActiveUsers(active);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // ---------- Filters ----------
  const matchesCategory = (item, cat) => {
    if (cat === 'All') return true;
    const haystack = `${item.specialization || ''} ${item.category || ''} ${item.title || ''} ${item.name || ''}`.toLowerCase();
    return haystack.includes(cat.toLowerCase());
  };

  const matchesSearch = (item, q) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (item.title || '').toLowerCase().includes(s) ||
      (item.name || '').toLowerCase().includes(s) ||
      (item.brand || '').toLowerCase().includes(s) ||
      (item.category || '').toLowerCase().includes(s) ||
      (item.specialization || '').toLowerCase().includes(s) ||
      (item.description || '').toLowerCase().includes(s) ||
      (item.pro?.businessName || '').toLowerCase().includes(s) ||
      (item.pro?.name || '').toLowerCase().includes(s)
    );
  };

  // Attach distance (km) based on pro coords, when client location is available
  const withDistance = (item) => {
    if (!currentLocation?.lat || !item.pro?.coordinates) {
      return { ...item, distance: null };
    }
    const d = haversineKm(
      currentLocation.lat,
      currentLocation.lng,
      item.pro.coordinates.lat,
      item.pro.coordinates.lng
    );
    return { ...item, distance: d };
  };

  // Sort: items with distance first (ascending), then the rest
  const sortByDistance = (arr) => {
    if (!currentLocation?.lat) return arr;
    return [...arr].sort((a, b) => {
      const da = a.distance ?? Infinity;
      const db = b.distance ?? Infinity;
      return da - db;
    });
  };

  const filteredServices = useMemo(() => {
    let list = services
      .filter(s => matchesCategory(s, activeCategory))
      .filter(s => matchesSearch(s, searchQuery));

    if (currentLocation?.lat) {
      list = list.map(withDistance);
      if (nearMeOnly) list = list.filter(s => s.distance != null && s.distance <= NEAR_ME_RADIUS_KM);
      list = sortByDistance(list);
    } else if (nearMeOnly) {
      list = []; // no location → Near Me filter yields nothing
    }
    return list;
  }, [services, activeCategory, searchQuery, currentLocation, nearMeOnly]);

  const filteredProducts = useMemo(() => {
    let list = products
      .filter(p => matchesCategory(p, activeCategory))
      .filter(p => matchesSearch(p, searchQuery));

    if (currentLocation?.lat) {
      list = list.map(withDistance);
      if (nearMeOnly) list = list.filter(p => p.distance != null && p.distance <= NEAR_ME_RADIUS_KM);
      list = sortByDistance(list);
    } else if (nearMeOnly) {
      list = [];
    }
    return list;
  }, [products, activeCategory, searchQuery, currentLocation, nearMeOnly]);

  const totalResults =
    (activeType === 'all' || activeType === 'services' ? filteredServices.length : 0) +
    (activeType === 'all' || activeType === 'products' ? filteredProducts.length : 0);

  // True when Near Me is on but nothing matched while data exists — show a helpful empty state
  const isNearMeEmpty =
    nearMeOnly &&
    totalResults === 0 &&
    (services.length > 0 || products.length > 0) &&
    !searchQuery &&
    activeCategory === 'All';

  // ---------- Handlers ----------
  const handleCategoryClick = (catId) => setActiveCategory(catId);
  const handleProClick = (pro) => navigate(`/professional/${pro.id}`);

  // ============================================================
  // FIXED: Inject live pro data before opening the modal
  // ============================================================
  const handleServiceClick = (service) => {
    const livePro = pros.find(p => p.id === service.professionalId);
    setPreviewItem({
      type: 'service',
      item: {
        ...service,
        professionalName: livePro?.businessName || livePro?.name || service.professionalName,
        professionalImage: livePro?.profileImage || livePro?.coverImage || service.professionalImage,
        pro: livePro || service.pro
      }
    });
  };

  const handleProductClick = (product) => {
    const livePro = pros.find(p => p.id === product.professionalId);
    setPreviewItem({
      type: 'product',
      item: {
        ...product,
        professionalName: livePro?.businessName || livePro?.name || product.professionalName,
        professionalImage: livePro?.profileImage || livePro?.coverImage || product.professionalImage,
        pro: livePro || product.pro
      }
    });
  };
  // ============================================================

  const handleBookService = (service) => {
    if (!service?.pro?.id) return;
    
    // Guard: Prevent booking yourself
    if (service.professionalId === user?.uid || service.pro?.id === user?.uid) {
      showToast("You cannot book your own service.", 'error');
      return;
    }

    setBookingService(service);
    setBookingPro(service.pro);
  };

  const handleAddProduct = async (e, product) => {
    e?.stopPropagation();
    
    // Guard: Prevent buying yourself
    if (product.professionalId === user?.uid) {
      showToast("You cannot buy your own product.", 'error');
      return;
    }

    try {
      await addToCart(product, 1);
      showToast(`Added ${product.name}`);
    } catch {
      showToast('Failed to add', 'error');
    }
  };

  const handleToggleFavorite = (itemId) => {
    setFavoriteIds(prev =>
      prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );
  };

  const closePreview = () => setPreviewItem(null);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getServiceCover = (service) => {
    if (Array.isArray(service.media) && service.media.length > 0) {
      const first = service.media[0];
      if (first.type === 'image') return first.url;
    }
    if (service.media?.url) return service.media.url;
    if (service.mediaLegacy?.url) return service.mediaLegacy.url;
    return null;
  };

  const getProductCover = (product) => {
    if (product.image) return product.image;
    if (Array.isArray(product.media) && product.media.length > 0) {
      const first = product.media[0];
      if (first.type === 'image') return first.url;
    }
    if (product.mediaLegacy?.url) return product.mediaLegacy.url;
    return null;
  };

  const getProImage = (pro) => pro?.profileImage || pro?.coverImage || null;

  const priceFrom = (service) => {
    if (Array.isArray(service.options) && service.options.length > 0) {
      return Math.min(...service.options.map(o => Number(o.price) || 0));
    }
    return Number(service.price) || 0;
  };

  // Small distance chip component
  const DistanceChip = ({ distance, className = '' }) => {
    if (distance == null) return null;
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold text-gray-500 ${className}`}>
        <Navigation size={9} className="text-[#EFA9B6]" />
        {distance < 1 ? `${(distance * 1000).toFixed(0)} m` : `${distance.toFixed(1)} km`}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ============================================================
  // Active users pill bar
  // ============================================================
  const ActiveUsersPillBar = ({ compact = false }) => {
    if (activeUsers.length === 0) return null;

    return (
      <div className={compact ? 'mb-6' : 'mb-8'}>
        <div className="flex items-center justify-between mb-3">
          <h2 className={`font-bold text-gray-900 tracking-wide uppercase flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            Active Now
          </h2>
          <span className={`text-gray-400 ${compact ? 'text-[10px]' : 'text-xs'}`}>
            {activeUsers.length} online
          </span>
        </div>
        <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-2">
          {activeUsers.map((userItem) => {
            // Distance for active pro (if available)
            let d = null;
            if (currentLocation?.lat && userItem.coordinates) {
              d = haversineKm(
                currentLocation.lat,
                currentLocation.lng,
                userItem.coordinates.lat,
                userItem.coordinates.lng
              );
            }
            return (
              <button
                key={userItem.id}
                onClick={() => handleProClick(userItem)}
                className={`flex items-center gap-3 bg-white border border-gray-100 rounded-full shadow-sm hover:shadow-md hover:border-[#EFA9B6]/50 transition-all shrink-0 group ${
                  compact ? 'pl-1 pr-4 py-1 gap-2' : 'pl-1.5 pr-5 py-1.5'
                }`}
              >
                <div className={`relative rounded-full overflow-hidden bg-[#FFF5F6] shrink-0 ${compact ? 'w-9 h-9' : 'w-10 h-10'}`}>
                  {userItem.profileImage ? (
                    <img src={userItem.profileImage} alt={userItem.businessName || userItem.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#EFA9B6] to-[#CDBCE7] text-white font-bold text-xs">
                      {getInitials(userItem.businessName || userItem.name)}
                    </div>
                  )}
                  <span className={`absolute bottom-0 right-0 bg-green-500 border-2 border-white rounded-full ${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'}`}></span>
                </div>
                <div className="text-left min-w-0">
                  <p className={`font-bold text-gray-900 truncate ${compact ? 'text-xs max-w-[80px]' : 'text-sm max-w-[120px]'}`}>
                    {userItem.businessName || userItem.name}
                  </p>
                  <div className="flex items-center gap-1">
                    <p className={`text-gray-400 font-medium truncate ${compact ? 'text-[9px] max-w-[80px]' : 'text-[10px] max-w-[120px]'}`}>
                      {userItem.specialization || userItem.category || 'Pro'}
                    </p>
                    {d != null && (
                      <span className={`text-gray-400 ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
                        · {d < 1 ? `${(d * 1000).toFixed(0)}m` : `${d.toFixed(1)}km`}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ============================================================
  // Service rectangle (desktop)
  // ============================================================
  const ServiceRectangle = ({ service }) => {
    const cover = getServiceCover(service);
    const hasOptions = Array.isArray(service.options) && service.options.length > 0;
    const mediaCount = Array.isArray(service.media) ? service.media.length : (cover ? 1 : 0);

    return (
      <button
        onClick={() => handleServiceClick(service)}
        className="w-full text-left bg-white rounded-3xl border border-gray-100 hover:border-[#EFA9B6] transition-colors overflow-hidden group"
      >
        <div className="flex items-stretch">
          <div className="w-28 sm:w-32 shrink-0 relative bg-[#FCF8F9] overflow-hidden">
            {cover ? (
              <img src={cover} alt={service.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Sparkles size={28} className="text-[#EFA9B6]" />
              </div>
            )}
            {mediaCount > 1 && (
              <span className="absolute bottom-2 left-2 bg-white/90 text-gray-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                +{mediaCount - 1}
              </span>
            )}
          </div>

          <div className="flex-1 min-w-0 p-4">
            <div className="flex items-start justify-between gap-2">
              {service.category && (
                <span className="text-[10px] font-bold tracking-wider text-[#EFA9B6] uppercase">
                  {service.category}
                </span>
              )}
              <DistanceChip distance={service.distance} />
            </div>
            <h3 className="font-bold text-gray-900 text-base truncate mt-0.5">
              {service.title}
            </h3>

            {service.pro && (
              <div className="flex items-center gap-1.5 mt-1">
                <div className="w-4 h-4 rounded-full bg-[#FFF5F6] flex items-center justify-center overflow-hidden shrink-0">
                  {getProImage(service.pro) ? (
                    <img src={getProImage(service.pro)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User size={8} className="text-[#EFA9B6]" />
                  )}
                </div>
                <p className="text-[11px] text-gray-500 truncate">
                  {service.pro.businessName || service.pro.name}
                </p>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 mt-2">
              {service.duration && (
                <span className="flex items-center gap-1">
                  <Clock size={11} />
                  {service.duration}
                </span>
              )}
              {service.locationMode === 'mobile' && (
                <span className="flex items-center gap-1 text-[#EFA9B6] font-bold">
                  <Car size={11} />
                  Travels to you
                </span>
              )}
              {hasOptions && (
                <span className="flex items-center gap-1 text-[#EFA9B6] font-bold">
                  {service.options.length} options
                </span>
              )}
            </div>

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider">
                  {hasOptions || service.variablePrice ? 'From' : 'Price'}
                </p>
                <p className="font-bold text-gray-900">R{priceFrom(service).toFixed(0)}</p>
              </div>
              <div className="flex items-center gap-1 text-[#EFA9B6] font-bold text-xs">
                Book
                <ChevronRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      </button>
    );
  };

  // ============================================================
  // Product square (desktop)
  // ============================================================
  const ProductSquare = ({ product }) => {
    const img = getProductCover(product);
    const isOut = product.stock === 0;

    return (
      <button
        onClick={() => handleProductClick(product)}
        className="bg-white rounded-3xl border border-gray-100 hover:border-[#EFA9B6] transition-colors overflow-hidden text-left group flex flex-col"
      >
        <div className="relative w-full aspect-square bg-[#FCF8F9] overflow-hidden">
          {img ? (
            <img
              src={img}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package size={28} className="text-gray-300" />
            </div>
          )}

          {isOut && (
            <div className="absolute bottom-2 left-2 bg-[#F4EEFF] text-[#AF8BD4] text-[9px] font-bold px-2 py-0.5 rounded-full">
              Sold out
            </div>
          )}
          {!isOut && product.stock > 0 && product.stock < 5 && (
            <div className="absolute bottom-2 left-2 bg-[#FFF3E8] text-[#B87333] text-[9px] font-bold px-2 py-0.5 rounded-full">
              {product.stock} left
            </div>
          )}

          <span
            onClick={(e) => handleAddProduct(e, product)}
            className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors shadow-sm cursor-pointer ${
              isOut
                ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                : 'bg-white text-[#EFA9B6] hover:bg-[#FFF5F6]'
            }`}
          >
            <Plus size={14} />
          </span>
        </div>

        <div className="p-3 flex-1 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            {product.category && (
              <span className="text-[9px] font-bold tracking-wider text-[#EFA9B6] uppercase">
                {product.category}
              </span>
            )}
            <DistanceChip distance={product.distance} />
          </div>
          <h3 className="font-bold text-gray-900 text-sm truncate mt-0.5">
            {product.name}
          </h3>
          {product.brand && (
            <p className="text-[10px] text-gray-400 truncate">{product.brand}</p>
          )}
          {product.pro && (
            <div className="flex items-center gap-1 mt-1">
              <div className="w-3 h-3 rounded-full bg-[#FFF5F6] flex items-center justify-center overflow-hidden shrink-0">
                {getProImage(product.pro) ? (
                  <img src={getProImage(product.pro)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User size={6} className="text-[#EFA9B6]" />
                )}
              </div>
              <p className="text-[10px] text-gray-500 truncate">
                {product.pro.businessName || product.pro.name}
              </p>
            </div>
          )}
          <p className="font-bold text-gray-900 text-sm mt-2">
            R{Number(product.price || 0).toFixed(2)}
          </p>
        </div>
      </button>
    );
  };

  // ============================================================
  // Near Me empty state (shared)
  // ============================================================
  const NearMeEmptyState = ({ compact = false }) => (
    <div className={compact ? 'text-center py-10' : 'text-center py-16'}>
      <div className={`bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4 ${compact ? 'w-16 h-16' : 'w-20 h-20'}`}>
        <Navigation size={compact ? 24 : 32} className="text-[#EFA9B6]" />
      </div>
      <h3 className={`font-serif font-bold text-gray-900 mb-2 ${compact ? 'text-base' : 'text-lg'}`}>
        Nothing within {NEAR_ME_RADIUS_KM}km
      </h3>
      <p className="text-gray-400 text-sm max-w-xs mx-auto mb-4">
        No pros near you matched these filters. Try turning off Near Me to see everything.
      </p>
      <button
        onClick={() => setNearMeOnly(false)}
        className="px-5 py-2.5 rounded-full bg-[#EFA9B6] text-white text-sm font-bold hover:bg-[#E68FA0] transition-colors"
      >
        Show all
      </button>
    </div>
  );

  // ============================================================
  // MOBILE RESULTS
  // ============================================================
  const MobileResults = () => {
    if (isNearMeEmpty) {
      return <NearMeEmptyState compact />;
    }

    if (totalResults === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <Search size={32} className="text-[#EFA9B6]" />
          </div>
          <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-400 text-sm">Try adjusting your search or filters</p>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {activeType !== 'products' && filteredServices.length > 0 && (
          <section>
            <h2 className="text-[11px] font-bold text-gray-900 tracking-[0.15em] uppercase mb-3">
              Services · {filteredServices.length}
            </h2>
            <div className="space-y-3">
              {filteredServices.map(service => {
                const cover = getServiceCover(service);
                const hasOptions = Array.isArray(service.options) && service.options.length > 0;

                return (
                  <button
                    key={service.id}
                    onClick={() => handleServiceClick(service)}
                    className="w-full text-left bg-white rounded-[2rem] p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-50 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all flex items-center gap-4 group"
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-serif font-bold text-gray-900 mb-1 leading-tight truncate">
                        {service.title}
                      </h3>
                      <div className="flex items-center gap-2 mb-2">
                        <p className="text-[10px] text-gray-500 leading-relaxed truncate">
                          {service.category || service.specialization || 'Service'}
                          {service.duration && ` · ${service.duration}`}
                        </p>
                        <DistanceChip distance={service.distance} />
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-sm font-bold text-gray-900 flex items-center gap-1">
                          {(hasOptions || service.variablePrice) && (
                            <span className="text-[#EFA9B6] text-xs font-bold">From </span>
                          )}
                          R{priceFrom(service).toFixed(0)}
                        </span>
                        <div className="bg-[#FFF5F6] text-[#EFA9B6] px-3 py-1 rounded-full text-[10px] font-bold">
                          Book
                        </div>
                      </div>
                    </div>

                    <div className="w-24 h-24 rounded-2xl p-1.5 shadow-sm group-hover:scale-105 transition-transform duration-300 relative overflow-hidden bg-[#FFF5F6] shrink-0">
                      {cover ? (
                        <img src={cover} className="w-full h-full object-cover rounded-xl shadow-inner" alt={service.title} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#FFF5F6] rounded-xl">
                          <Sparkles size={28} className="text-[#EFA9B6]" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {activeType !== 'services' && filteredProducts.length > 0 && (
          <section>
            <h2 className="text-[11px] font-bold text-gray-900 tracking-[0.15em] uppercase mb-3">
              Products · {filteredProducts.length}
            </h2>

            <div className="grid grid-cols-2 gap-4">
              {filteredProducts.map(product => {
                const img = getProductCover(product);
                const isOut = product.stock === 0;

                return (
                  <button
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    className="bg-white rounded-[2rem] p-3 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-50 flex flex-col relative text-left hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all"
                  >
                    <span
                      onClick={(e) => handleAddProduct(e, product)}
                      className={`absolute top-3 right-3 z-10 w-8 h-8 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-sm cursor-pointer ${
                        isOut ? 'opacity-50 cursor-not-allowed text-gray-300' : 'text-[#EFA9B6] hover:bg-[#FFF5F6]'
                      }`}
                    >
                      <Plus size={16} />
                    </span>

                    <div className="w-full aspect-square rounded-2xl mb-3 bg-gradient-to-b from-[#F8F9FA] to-white flex items-center justify-center overflow-hidden relative">
                      {img ? (
                        <img src={img} className="w-full h-full object-cover" alt={product.name} />
                      ) : (
                        <Package size={32} className="text-gray-300" />
                      )}

                      {isOut && (
                        <span className="absolute bottom-2 left-2 bg-[#F4EEFF] text-[#AF8BD4] text-[9px] font-bold px-2 py-0.5 rounded-full">
                          Sold out
                        </span>
                      )}
                    </div>

                    <h3 className="font-bold text-gray-900 text-sm mb-0.5 truncate">
                      {product.name}
                    </h3>
                    {product.brand && (
                      <p className="text-[10px] text-gray-500 mb-2 truncate">{product.brand}</p>
                    )}
                    <div className="flex items-center justify-between mt-auto">
                      <span className="font-bold text-gray-900 text-sm">
                        R{Number(product.price || 0).toFixed(2)}
                      </span>
                      <DistanceChip distance={product.distance} />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}
      </div>
    );
  };

  // ============================================================
  // DESKTOP RESULTS
  // ============================================================
  const DesktopResults = () => {
    if (isNearMeEmpty) {
      return <NearMeEmptyState />;
    }

    if (totalResults === 0) {
      return (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <Search size={32} className="text-[#EFA9B6]" />
          </div>
          <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">No results found</h3>
          <p className="text-gray-400 text-sm">Try adjusting your search or filters</p>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        {activeType !== 'products' && filteredServices.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-gray-900 tracking-wide uppercase">
                Services <span className="text-gray-400 font-normal">({filteredServices.length})</span>
              </h2>
            </div>
            <div className="space-y-3">
              {filteredServices.map(s => <ServiceRectangle key={s.id} service={s} />)}
            </div>
          </section>
        )}

        {activeType !== 'services' && filteredProducts.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-gray-900 tracking-wide uppercase">
                Products <span className="text-gray-400 font-normal">({filteredProducts.length})</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredProducts.map(p => <ProductSquare key={p.id} product={p} />)}
            </div>
          </section>
        )}
      </div>
    );
  };

  // ============================================================
  // Near Me pill (shared between desktop & mobile)
  // ============================================================
  const NearMePill = ({ compact = false }) => {
    const disabled = !currentLocation?.lat;
    return (
      <button
        onClick={() => !disabled && setNearMeOnly(v => !v)}
        disabled={disabled}
        title={disabled ? 'Enable location to use Near Me' : `Show only pros within ${NEAR_ME_RADIUS_KM}km`}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all ${
          disabled
            ? 'bg-white text-gray-300 border border-gray-100 cursor-not-allowed'
            : nearMeOnly
            ? 'bg-[#EFA9B6] text-white border border-[#EFA9B6] shadow-sm'
            : 'bg-white text-gray-600 border border-gray-100 hover:bg-gray-50'
        } ${compact ? 'text-xs px-3 py-1.5' : ''}`}
      >
        <Navigation size={compact ? 12 : 14} />
        Near Me ({NEAR_ME_RADIUS_KM}km)
      </button>
    );
  };

  // ============================================================
  // DESKTOP VIEW
  // ============================================================
  const DesktopView = () => (
    <div className="hidden lg:block max-w-6xl mx-auto">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold text-gray-900">Discover</h1>
          <p className="text-gray-500 mt-1">
            {totalResults} result{totalResults === 1 ? '' : 's'}
            {currentLocation?.lat && !nearMeOnly && (
              <span className="text-gray-400"> · sorted by distance</span>
            )}
          </p>
        </div>
      </div>

      <ActiveUsersPillBar />

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search for services or products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-100 rounded-2xl text-sm focus:outline-none focus:border-[#EFA9B6] focus:ring-2 focus:ring-[#EFA9B6]/20 transition-all"
          />
        </div>
        <button className="px-6 py-3.5 bg-[#FFF5F6] text-[#EFA9B6] rounded-2xl text-sm font-bold hover:bg-[#FBE1E8] transition-colors flex items-center gap-2 whitespace-nowrap">
          <Filter size={18} />
          Filters
        </button>
      </div>

      <div className="flex gap-1 p-1 bg-white border border-gray-100 rounded-full mb-6 max-w-md">
        {types.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveType(t.id)}
            className={`flex-1 py-2 rounded-full text-sm font-bold transition-colors ${
              activeType === t.id ? 'bg-[#FFF5F6] text-[#EFA9B6]' : 'text-gray-500 hover:bg-[#FCF8F9]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-6 pb-2">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => handleCategoryClick(cat.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              activeCategory === cat.id
                ? 'bg-[#FFF5F6] text-[#EFA9B6] border border-[#FBE1E8]'
                : 'bg-white text-gray-600 border border-gray-100 hover:bg-gray-50'
            }`}
          >
            {cat.icon}
            {cat.label}
          </button>
        ))}
        <NearMePill />
      </div>

      <DesktopResults />
    </div>
  );

  // ============================================================
  // MOBILE VIEW
  // ============================================================
  const MobileView = () => (
    <div className="lg:hidden h-full flex flex-col bg-[#FCF8F9] overflow-hidden font-sans">
      <div className="bg-white pt-12 pb-6 px-6 rounded-b-[3rem] shadow-[0_10px_30px_rgba(0,0,0,0.03)] border-b border-gray-50 z-10 relative">
        <div className="flex gap-3 mb-6">
          <div className="flex-1 bg-[#FCF8F9] rounded-2xl flex items-center px-4 py-3 shadow-inner border border-gray-100">
            <Search size={18} className="text-gray-400 mr-3" />
            <input
              type="text"
              placeholder="Search services or products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none w-full text-gray-700 placeholder:text-gray-400 text-sm font-medium"
            />
          </div>
          <button className="w-11 h-11 bg-[#FFF5F6] text-[#EFA9B6] rounded-2xl flex items-center justify-center hover:bg-[#FBE1E8] transition-colors shadow-sm shrink-0">
            <Filter size={20} />
          </button>
        </div>

        <ActiveUsersPillBar compact />

        <div className="flex gap-1 p-1 bg-[#FCF8F9] border border-gray-100 rounded-full mb-4">
          {types.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveType(t.id)}
              className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${
                activeType === t.id ? 'bg-[#FFF5F6] text-[#EFA9B6]' : 'text-gray-500'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex justify-between items-center px-1">
          {categories.map((cat) => (
            <div
              key={cat.id}
              onClick={() => handleCategoryClick(cat.id)}
              className="flex flex-col items-center gap-2 cursor-pointer group"
            >
              <div className={`w-[3.2rem] h-[3.2rem] rounded-[1rem] flex items-center justify-center transition-all duration-300 shadow-sm ${
                activeCategory === cat.id
                  ? 'bg-[#FFF5F6] text-[#EFA9B6] border border-[#FBE1E8]'
                  : 'bg-white border border-gray-50 text-gray-400 group-hover:bg-gray-50'
              }`}>
                {cat.icon}
              </div>
              <span className={`text-[10px] font-bold ${
                activeCategory === cat.id ? 'text-[#EFA9B6]' : 'text-gray-400'
              }`}>
                {cat.label}
              </span>
            </div>
          ))}
        </div>

        {/* Near Me toggle — mobile */}
        <div className="mt-4 flex justify-center">
          <NearMePill compact />
        </div>
      </div>

      <div className="px-6 pt-6 pb-32 flex-1 overflow-y-auto">
        <MobileResults />
      </div>
    </div>
  );

  return (
    <>
      <DesktopView />
      <MobileView />

      {previewItem && (
        <ItemDetailModal
          type={previewItem.type}
          item={previewItem.item}
          onClose={closePreview}
          onAddToCart={(product, qty) => {
            addToCart(product, qty);
            showToast(`Added ${product.name}`);
          }}
          onBook={(service) => {
            closePreview();
            handleBookService(service);
          }}
          onToggleFavorite={handleToggleFavorite}
          isFavorite={favoriteIds.includes(previewItem.item.id)}
        />
      )}

      {bookingService && bookingPro && (
        <BookingCalendarModal
          service={bookingService}
          professional={bookingPro}
          onClose={() => {
            setBookingService(null);
            setBookingPro(null);
          }}
        />
      )}

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[110]">
          <div className={`px-5 py-3 rounded-full shadow-lg text-sm font-bold ${
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-[#FFF5F6] text-[#EFA9B6]'
          }`}>
            {toast.msg}
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </>
  );
};

export default SearchPage;